import { FieldHelpTooltip } from '@/Domain/components/Integration/components/FieldHelpTooltip';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
} from '@/Domain/components/Shared/Field/Field';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';

interface IntegrationToggleFieldProps {
  id: string;
  label: string;
  description?: string;
  /** Shown in a tooltip behind a help icon next to the label. */
  tooltip?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Shows a spinner to the left of the switch (e.g. while the change is being applied), without shifting layout. */
  loading?: boolean;
  'data-testid'?: string;
}

/**
 * A standardized labeled switch row: 14px label + 12px description on the left,
 * switch on the right. Used for the Enable toggle and any on/off setting.
 */
export const IntegrationToggleField = ({
  id,
  label,
  description,
  tooltip,
  checked,
  onCheckedChange,
  disabled,
  loading,
  ...props
}: IntegrationToggleFieldProps) => (
  <Field orientation="horizontal" className="items-center">
    <FieldContent>
      <div className="flex items-center gap-1.5">
        <FieldLabel htmlFor={id} className="font-medium">
          {label}
        </FieldLabel>
        {tooltip ? <FieldHelpTooltip content={tooltip} data-testid={`${id}-help`} /> : null}
      </div>
      {description ? <FieldDescription className="pb-0">{description}</FieldDescription> : null}
    </FieldContent>
    <div className="relative flex items-center">
      {loading ? (
        <Spinner
          className="absolute right-full top-1/2 mr-2 -translate-y-1/2 size-3.5 text-foreground-subtle"
          data-testid={`${id}-loader`}
        />
      ) : null}
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled}
        data-testid={props['data-testid']}
      />
    </div>
  </Field>
);

export default IntegrationToggleField;
